'use client';
import {
  cloneElement,
  createContext,
  useContext,
  useId,
  type ComponentProps,
  type ReactElement,
} from 'react';
import {
  Controller,
  FormProvider,
  useFormContext,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { useTranslations } from 'next-intl';
import { cn } from '~/lib/utils';
const FieldContext = createContext<string | null>(null);
const ItemContext = createContext<string | null>(null);
export const Form = FormProvider;
export function FormField<T extends FieldValues, N extends FieldPath<T>>(
  props: ControllerProps<T, N>,
) {
  return (
    <FieldContext value={props.name}>
      <Controller {...props} />
    </FieldContext>
  );
}
function useField() {
  const name = useContext(FieldContext);
  const id = useContext(ItemContext);
  const form = useFormContext();
  if (!name || !id) throw new Error('Field controls require FormField and FormItem');
  return { id, error: form.getFieldState(name, form.formState).error };
}
export function FormItem({ className, ...props }: ComponentProps<'div'>) {
  const id = useId();
  return (
    <ItemContext value={id}>
      <div className={cn('grid gap-2', className)} {...props} />
    </ItemContext>
  );
}
export function FormLabel({ className, ...props }: ComponentProps<'label'>) {
  const { id, error } = useField();
  return (
    <label
      htmlFor={`${id}-control`}
      className={cn('text-sm font-semibold', error && 'text-destructive', className)}
      {...props}
    />
  );
}
export function FormControl({
  children,
}: {
  children: ReactElement<{ id?: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }>;
}) {
  const { id, error } = useField();
  return cloneElement(children, {
    id: `${id}-control`,
    'aria-invalid': !!error,
    'aria-describedby': `${id}-description${error ? ` ${id}-message` : ''}`,
  });
}
export function FormDescription({ className, ...props }: ComponentProps<'p'>) {
  const { id } = useField();
  return (
    <p
      id={`${id}-description`}
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}
export function FormMessage({ children, className, ...props }: ComponentProps<'p'>) {
  const { id, error } = useField();
  const t = useTranslations();
  const message = error?.message;
  const content = message ? (t.has(message) ? t(message) : message) : children;
  return content ? (
    <p
      id={`${id}-message`}
      role="alert"
      className={cn('text-destructive text-sm', className)}
      {...props}
    >
      {content}
    </p>
  ) : null;
}
